import { useQueryClient } from "@tanstack/react-query";
import { max, min } from "date-fns";
import { buildAddonGtmItemsForCartItem } from "@/features/addons/lib/addon-gtm-item";
import { fetchDietAddonsByDietId } from "@/features/addons/lib/fetch-diet-addons";
import type { SubItemsPricing } from "@/features/addons/types/sub-Items-pricing.type";
import { getDiet } from "@/features/diets/api/get-diet";
import type { DietCatalogDetail } from "@/features/diets/types";
import type { ConfigurationItem } from "../../cart/types";
import { sendEvent } from "../gtm/event";
import type { EventAddToCartType } from "../types/event.type";

type AllowedEvents =
  | "add_to_cart"
  | "remove_from_cart"
  | "view_cart"
  | "begin_checkout";

type ConfigurationItemWithPrice = ConfigurationItem & {
  price?: number;
  quantity?: number;
  duration?: number;
  // Addon quote of the position — the only place the discounted addon prices
  // live, the configuration item itself carries no amounts (MPL-3873).
  subItemsPricing?: SubItemsPricing | null;
};

export const useEventCart = () => {
  const queryClient = useQueryClient();

  const sendCertEvent = async ({
    eventName,
    diets,
    value,
    other,
  }: {
    eventName: AllowedEvents;
    diets: ConfigurationItemWithPrice[];
    value: number;
    other?: Partial<EventAddToCartType["ecommerce"]> & {
      coupon?: string;
      price?: number;
      quantity?: number;
    };
  }) => {
    const quueries: Promise<DietCatalogDetail>[] = [];
    const fetchData = async () => {
      for (const element of diets) {
        quueries.push(getDiet(element.dietId));
      }

      const results = await Promise.all(quueries);

      return results;
    };

    let fetchedDiets: DietCatalogDetail[];
    try {
      fetchedDiets = await fetchData();
    } catch (error) {
      // Without the catalog diets there is no event to build. Safari rejects
      // these fetches when the user leaves the page mid-request (Sentry
      // WEB-221), and an analytics event must not fail its caller — clearing
      // the cart awaits this one.
      console.warn(`GTM ${eventName} skipped: diets not loaded`, error);
      return;
    }

    // Addons of every position, resolved from the catalog: the cart contract
    // carries only their ids, dates and quantities (MPL-3730).
    const addonsByDietId = await fetchDietAddonsByDietId(
      queryClient,
      diets.filter((item) => item.subItems?.length).map((item) => item.dietId),
    );

    await sendEvent({
      event: eventName,
      ecommerce: {
        currency: "PLN",
        value,
        // Each position contributes its diet followed by its addons — a flat
        // list, since GA4 items have no parent-child relation.
        items: fetchedDiets.flatMap((diet, index) => {
          const original = diets[index];
          const firstDate = min(original?.dates || []);
          const lastDate = max(original?.dates || []);
          return [
            {
              item_id: diet?.id || "",
              item_name: diet?.name || "",
              item_brand: diet?.catering.name || "",
              item_variant: diet?.menuSelection ? "diet with choice" : "diet",
              item_category2: String(original?.calories),
              item_category3: firstDate.getTime(),
              item_category4: lastDate.getTime(),
              item_category: diet?.tags.map((tag) => tag.slug).join(", "),
              price: original?.price || 0,
              quantity: original?.quantity || 0,
              duration: original?.duration || 0,
            },
            ...buildAddonGtmItemsForCartItem({
              cateringName: diet?.catering.name,
              categories: addonsByDietId.get(original?.dietId ?? "") ?? [],
              subItems: original?.subItems,
              subItemsPricing: original?.subItemsPricing,
            }),
          ];
        }),
        ...other,
      },
    });
  };

  return { sendCertEvent };
};
